// @ts-nocheck
import { getBackendUrl } from "@Utils";
import { useTranslation } from "react-i18next";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom/client";
import DropDownListComponent from "../../../cubeComponent/form/DropdownListComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import SlickGrid from "../../../cubeComponent/html/SlickGrid";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";

interface SelectedUsers {
  id: string;
  spaceId: string;
  spaceName: string;
}

interface Space {
  _id: string;
  description: string;
  spaceName: string;
  createdDate: number;
  status: string;
  id: string;
  createdBy: string;
  spaceId: string;
  projectId: string;
  createdByName: string;
}

type SelectedSpace = Pick<Space, "_id" | "spaceName">;

interface SpaceUserSelecterProps {
  spaceList: Space[];
  onSpaceSelectorModalClose: () => void;
  onConfirm: () => void;
  selectedUsers: SelectedUsers[];
}

interface OueryParams {
  spacesList: Array<String>;
  params: {
    spacesList: Array<String>;
  };
}

function SpaceUserSelector({
  spaceList,
  onSpaceSelectorModalClose,
  onConfirm,
  selectedUsers = [],
}: SpaceUserSelecterProps) {
  const { t } = useTranslation();
  // useRef Declarations
  const spaceRef = useRef<any>(null);
  const spaceUsersRef = useRef<any>(null);

  // State Declarations
  const [selectedSpace, setSelectedSpace] = useState<SelectedSpace | null>(null);
  const [selectedUsersList, setSelectedUsersList] = useState<SelectedUsers[]>(selectedUsers);
  const [isAddToListButtonDisabled, setIsAddToListButtonDisabled] = useState<boolean>(true);
  const [spaceUsersQuery, setSpaceUsersQuery] = useState<OueryParams | null>(null);
  const disabledUserIds = useMemo(() => selectedUsersList.map((user) => user.id), [selectedUsersList]);
  const hasAddedNewUsers = useMemo(() => {
    if (selectedUsersList.length <= selectedUsers.length) {
      return false;
    }

    const originalIds = new Set(selectedUsers.map((user) => user.id));
    const hasNewUser = selectedUsersList.some((user) => !originalIds.has(user.id));

    return hasNewUser;
  }, [selectedUsersList, selectedUsers]);

  // useEffect to set default space when the Component mounts and set query to fetch users of the space
  useEffect(() => {
    if (spaceList?.length > 0) {
      const defaultSpace = spaceList[0];
      setSpaceUsersQuery({
        spacesList: [defaultSpace._id],
        params: {
          spacesList: [defaultSpace._id],
        },
      });
      setSelectedSpace({
        _id: defaultSpace._id,
        spaceName: defaultSpace.spaceName,
      });
    }
  }, [spaceList]);

  // Processing spaceUsers before passing to the Grid
  const processResponseFunction = (args) => {
    const processedData = args.data.result.map((user) => ({
      id: `${user.id}_${user.spaceId}`,
      userId: user.userId,
      userName: user.userName,
      email: user.email,
      company: user.company,
      designation: user.designation,
      spaceId: user.spaceId,
      spaceName: user.spaceName,
    }));
    return processedData;
  };

  // Data Manager for Space Users

  // Function to set the selected space and related users in the Grid
  const changeSelectedSpace = (event) => {
    if (!event.value) return;

    const dropdownSelectedSpace = spaceList.find((space) => space._id === event.value);

    if (dropdownSelectedSpace) {
      setSpaceUsersQuery({
        spacesList: [dropdownSelectedSpace._id],
        params: {
          spacesList: [dropdownSelectedSpace._id],
        },
      });
      setSelectedSpace({
        _id: dropdownSelectedSpace._id,
        spaceName: dropdownSelectedSpace.spaceName,
      });
      setTimeout(() => {
        if (spaceUsersRef.current && spaceUsersRef.current.slickGrid) {
          spaceUsersRef.current.slickGrid.setSelectedRows([]);
          setIsAddToListButtonDisabled(true);
        }
      }, 0);
    }
  };

  // Handler function to exit the modal when user clicks outside the modal
  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onSpaceSelectorModalClose();
      }
    },
    [onSpaceSelectorModalClose],
  );

  // Handler function to add selected users to the list
  const handleAddToList = useCallback(() => {
    const selectedItems = spaceUsersRef.current.dataView.getAllSelectedItems();
    setSelectedUsersList((prevList) => {
      const existingIds = new Set(prevList.map((item) => item.id));
      const newUsers = selectedItems
        .filter((item) => !existingIds.has(item.id))
        .map((item) => ({
          id: item.id,
          spaceId: item.spaceId,
          spaceName: item.spaceName,
        }));
      return [...prevList, ...newUsers];
    });
    if (spaceUsersRef.current && spaceUsersRef.current.slickGrid) {
      spaceUsersRef.current.slickGrid.setSelectedRows([]);
      setIsAddToListButtonDisabled(true);
    }
  }, []);

  // Handler function to select a row in the grid
  const handleRowSelection = () => {
    const selectedRows = spaceUsersRef.current.slickGrid.getSelectedRows();
    setIsAddToListButtonDisabled(selectedRows.length === 0);
  };

  // Handler function to remove users from the list
  const handleRemoveUser = useCallback(
    (id) => {
      setSelectedUsersList((prev) => {
        const updatedList = prev.filter((user) => user.id !== id);
        return updatedList;
      });
    },
    [selectedUsers],
  );

  // Handler function to confirm addition of users and close the modal
  const handleConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm(selectedUsersList);
    }
    onSpaceSelectorModalClose();
  }, [selectedUsersList, onConfirm, onSpaceSelectorModalClose]);

  // Space dropdown list
  const SpaceDropDownList = useCallback(
    () => (
      <DropDownListComponent
        ref={spaceRef}
        dataSource={spaceList}
        fields={{ text: "spaceName", value: "_id" }}
        onChange={changeSelectedSpace}
        value={selectedSpace?._id || ""}
        allowFiltering
      />
    ),
    [spaceList, selectedSpace?._id, changeSelectedSpace],
  );

  // Formatter function for User Name column in Grid
  const userNameTagFormatter = (row, cell, value, columnDef, dataContext) => {
    if (!value) return "";

    const container = document.createElement("div");
    ReactDOM.createRoot(container).render(<UserNameTagComponent id={dataContext.userId} />);

    return container;
  };

  // Function to render selected users for each space
  const selectedUsersChips = useMemo(() => {
    return selectedUsersList
      .filter((user) => user.spaceId === selectedSpace?._id)
      .map((user) => (
        <UserNameTagComponent
          key={user.id}
          id={user.id.split("_")[0]}
          handleDeleteIconClicked={() => handleRemoveUser(user.id)}
        />
      ));
  }, [selectedUsersList, selectedSpace?._id, handleRemoveUser]);

  // SlickGrid column definitions
  const columnDefinitions = useRef([
    {
      id: "userName",
      name: t("User Name"),
      field: "userName",
      formatter: userNameTagFormatter,
    },
    {
      id: "email",
      name: t("E-mail ID"),
      field: "email",
    },
    {
      id: "company",
      name: t("Company"),
      field: "company",
    },
    {
      id: "designation",
      name: t("Designation"),
      field: "designation",
    },
  ]);

  // JSX for the Component
  const modalHeader = useCallback(
    () => (
      <div className="flex items-center justify-between w-full py-2 px-6 bg-surface_stroke_gradient_color1 rounded-t-lg">
        <p className="text-base font-bold">{t("Add Users")}</p>
        <div className="flex items-center gap-4 ms-auto">
          <SpaceDropDownList />
          <IconComponent name="close" onClick={onSpaceSelectorModalClose} className="cursor-pointer" />
        </div>
      </div>
    ),
    [SpaceDropDownList, onSpaceSelectorModalClose],
  );

  const modalContent = useCallback(
    () => (
      <div className="bg-white">
        <div className="">
          <div style={{ height: "40vh" }}>
            {spaceUsersQuery && (
              <SlickGrid
                ref={spaceUsersRef}
                columnDefinitions={columnDefinitions.current}
                remoteDataSource={`${getBackendUrl()}/spaces/getUsersInSpaces`}
                query={spaceUsersQuery}
                enableFiltering={false}
                disabledIds={disabledUserIds}
                onClick={handleRowSelection}
                beforeDataBound={processResponseFunction}
                onSelectedRowsChanged={handleRowSelection}
                gridOptions={{
                  autosizeColumnsByCellContentOnFirstLoad: false,
                  enableAutoResizeColumnsByCellContent: false,
                  autoFitColumnsOnFirstLoad: true,
                  enableAutoSizeColumns: true,
                }}
                selectCheckBoxOnRowClick
              />
            )}
          </div>
        </div>
        <div className="flex py-4 px-6 gap-4">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={handleAddToList}
            disabled={isAddToListButtonDisabled}
          >
            {"+ " + t("Add to list")}
          </ButtonComponent>
          {selectedUsersList.length !== 0 && <div className="flex gap-4">{selectedUsersChips}</div>}
        </div>
      </div>
    ),
    [
      columnDefinitions,
      spaceUsersQuery,
      handleAddToList,
      selectedUsersList,
      selectedUsersChips,
      disabledUserIds,
      isAddToListButtonDisabled,
    ],
  );

  const modalFooter = useCallback(
    () => (
      <div className="flex w-full py-4 px-6 bg-surface_white border-t border-surface_stroke_gradient_color1 gap-4 rounded-b-lg">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleConfirm} disabled={!hasAddedNewUsers}>
          {t("Confirm")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={onSpaceSelectorModalClose}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    ),
    [handleConfirm, onSpaceSelectorModalClose, hasAddedNewUsers],
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-[100] flex flex-col items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="w-[60%] bg-white rounded-sg">
        {modalHeader()}
        {modalContent()}
        {modalFooter()}
      </div>
    </div>
  );
}

export default SpaceUserSelector;
